import { ComponentType, ReactElement, ReactNode } from 'react';

import { AppContext, AppContextData } from '../contexts/AppContext';
import { AttachmentManagementServiceProvider } from '../contexts/AttachmentManagementServiceContext';
import { AutoSavingServiceProvider } from '../contexts/AutoSavingServiceContext';
import { BarRaiserEvaluationServiceProvider } from '../contexts/BarRaiserEvaluationServiceContext';
import { ContentEditorServiceProvider } from '../contexts/ContentEditorServiceContext';
import { ContentValidatorProvider } from '../contexts/ContentValidatorProvider';
import { FatalErrorProvider } from '../contexts/FatalErrorContext';
import { InlineCommentManagerProvider } from '../contexts/InlineCommentManagerContext';
import { InlineEditsManagerProvider } from '../contexts/InlineEditsManagerContext';
import { MplServiceClientProvider } from '../contexts/MplServiceClientContext';
import { FindingReconcilerProvider } from '../contexts/FindingReconcilerContext';
import { PendingFlushCoordinatorProvider } from '../contexts/PendingFlushCoordinatorContext';
import { SuggestionNavigationServiceProvider } from '../contexts/SuggestionNavigationServiceContext';
import { ToolbarActionServiceProvider } from '../contexts/ToolbarActionServiceContext';
import { QualityCheckServiceProvider } from '../contexts/QualityCheckServiceContext';
import { PublisherReviewServiceProvider } from '../contexts/PublisherReviewServiceContext';
import { UserServiceProvider } from '../contexts/UserServiceContext';

interface AppProvidersProps {
  readonly appContext: AppContextData;
  readonly children: ReactNode;
}

/**
 * Declares global services available to the entire application through React context.
 *
 * Only register providers here for services that are true application-wide
 * singletons (e.g. authentication, editing, API clients). If a service is
 * only consumed by a single page or a narrow subtree of components, do NOT
 * add its provider here — instead, wrap it around the component(s) that
 * actually use it.
 */
function AppProviders({ appContext, children }: AppProvidersProps) {
  // Order matters: leaf services must be declared first.
  const providers: ComponentType<{ children: ReactNode; appContext?: AppContextData }>[] = [
    FatalErrorProvider,
    UserServiceProvider,
    MplServiceClientProvider,
    AutoSavingServiceProvider,
    PendingFlushCoordinatorProvider,
    ContentValidatorProvider,
    ContentEditorServiceProvider,
    AttachmentManagementServiceProvider,
    InlineCommentManagerProvider,
    ToolbarActionServiceProvider,
    InlineEditsManagerProvider,
    FindingReconcilerProvider,
    SuggestionNavigationServiceProvider,
    QualityCheckServiceProvider,
    PublisherReviewServiceProvider,
    BarRaiserEvaluationServiceProvider,
  ];

  // `AppContext.Provider` is kept as the innermost provider (closest to
  // `children`) so the other providers above cannot read from it, preserving
  // the original wrapping order. It is passed directly as the initial value
  // of `reduceRight` — rather than via an inline wrapper component — so that
  // its element type stays stable across renders. An inline wrapper would
  // create a new component type on every render, causing React to unmount and
  // remount the entire subtree whenever `AppProviders` re-renders.
  return providers.reduceRight<ReactElement>(
    (acc, Provider) => <Provider appContext={appContext}>{acc}</Provider>,
    <AppContext.Provider value={appContext}>{children}</AppContext.Provider>,
  );
}

export default AppProviders;
